import 'deep-chat';
import '@atlas/shared/client/ui/wc/components/button';
import '@atlas/shared/client/ui/wc/components/loading-overlay';
import '@atlas/shared/client/ui/wc/icons/IconHelp';
import '@atlas/shared/client/ui/wc/icons/brands/IconWhatsapp';
import '@atlas/shared/client/ui/wc/icons/IconChat';
import '@atlas/shared/client/ui/wc/icons/IconClose';
import '@atlas/shared/client/ui/wc/icons/IconLess';
import '@atlas/shared/client/ui/wc/icons/IconSmartphone';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { chatbotStyle } from './Chatbot.styles';
import { SuperElement } from '@atlas/shared/client/ui/wc/internals/super/SuperElement';
import { isMobileDevice } from '@atlas/shared/client/ui/core/utils';
import { animate, flyBelow } from '@lit-labs/motion';
import { t } from 'i18next';

@customElement('lr-chatbot-minimized')
export class ChatbotMinimized extends SuperElement {
    static styles = [SuperElement.styles, chatbotStyle];

    @property({ attribute: false }) open?: () => void;

    @state()
    private _isDragging = false;

    @state()
    private _chatbotMinimizedElement: HTMLElement = document.createElement('div');

    private _isClickEventRemoved = false;
    private _isEventDraggableSet = false;
    private _lastX = 0;
    private _lastY = 0;
    private _isMobileDevice: boolean = isMobileDevice();

    disconnectedCallback(): void {
        if (this._isMobileDevice) {
            this.removeMobileEventListener();
        }
    }

    firstUpdated() {
        if (this._isMobileDevice) {
            this.setElement();
        }
    }

    updated(changedProps: Map<string, unknown>) {
        if (changedProps.has('_isDragging')) {
            if (!this._isDragging && this._isClickEventRemoved) {
                setTimeout(() => {
                    this._isClickEventRemoved = false;
                }, 200);
            }
        }
        if (changedProps.has('_chatbotMinimizedElement')) {
            if (this._isMobileDevice) {
                if (this._chatbotMinimizedElement.classList.contains('isDraggable') && !this._isEventDraggableSet) {
                    this.setMobileEventListener();
                } else {
                    this.setElement();
                }
            }
        }
    }

    private handleTouchStart = (event: TouchEvent) => {
        event.stopPropagation();
        this._isDragging = true;
        this._lastX = event.touches[0].clientX;
        this._lastY = event.touches[0].clientY;
    };

    private handleTouchMove = (event: TouchEvent) => {
        event.stopPropagation();
        event.preventDefault();
        if (!this._isDragging) return;

        const deltaX = event.touches[0].clientX - this._lastX;
        const deltaY = event.touches[0].clientY - this._lastY;

        const elementX = parseInt(
            window.getComputedStyle(this._chatbotMinimizedElement as HTMLElement).getPropertyValue('left')
        );
        const elementY = parseInt(
            window.getComputedStyle(this._chatbotMinimizedElement as HTMLElement).getPropertyValue('top')
        );

        this._chatbotMinimizedElement.style.bottom = '';
        this._chatbotMinimizedElement.style.cursor = 'grabbing';
        this._chatbotMinimizedElement.style.left = `${elementX + deltaX}px`;
        this._chatbotMinimizedElement.style.top = `${elementY + deltaY}px`;

        this._lastX = event.touches[0].clientX;
        this._lastY = event.touches[0].clientY;

        if (!this._isClickEventRemoved) {
            this._isClickEventRemoved = true;
        }
    };

    private handleTouchEnd = (event: TouchEvent) => {
        event.stopPropagation();
        if (this._isDragging) {
            this._isDragging = false;
            this._chatbotMinimizedElement.style.cursor = 'grab';
        }
    };

    private setElement = async () => {
        const chatbot = document.querySelector('#chatbot');
        const shadowRoot1 = chatbot?.shadowRoot;
        const wrapper = shadowRoot1?.querySelector('#chat-minimizerd-wrapper') as HTMLElement;
        const shadowRoot2 = wrapper?.shadowRoot;
        const element = shadowRoot2?.querySelector('#chat-minimized.isDraggable') as HTMLElement;
        if (element instanceof HTMLElement) {
            this._chatbotMinimizedElement = element;
        } else {
            console.warn('Chatbot minimized element not found in shadow DOM');
        }
    };

    private setMobileEventListener = () => {
        if (this._chatbotMinimizedElement) {
            this._chatbotMinimizedElement.addEventListener('touchstart', this.handleTouchStart);
            this._chatbotMinimizedElement.addEventListener('touchmove', this.handleTouchMove);
            this._chatbotMinimizedElement.addEventListener('touchend', this.handleTouchEnd);
            this._isEventDraggableSet = true;
        }
    };

    private removeMobileEventListener = () => {
        if (this._chatbotMinimizedElement) {
            this._chatbotMinimizedElement.removeEventListener('touchstart', this.handleTouchStart);
            this._chatbotMinimizedElement.removeEventListener('touchmove', this.handleTouchMove);
            this._chatbotMinimizedElement.removeEventListener('touchend', this.handleTouchEnd);
            this._isEventDraggableSet = false;
        }
    };

    private handleClick = () => {
        if (!this._isClickEventRemoved) {
            if (this._isMobileDevice) this.removeMobileEventListener();
            this.open?.();
        }
    };

    renderElement() {
        return html`
            <div
                id="chat-minimized"
                class=${this._isMobileDevice ? 'isDraggable' : ''}
                @click=${this.handleClick}
                aria-hidden="false"
                aria-label="${t('chatbot:minimized.ariaLabel')}"
                aria-expanded="false"
                part="wrapper-minimized"
                ${animate({
                    in: flyBelow,
                    out: flyBelow
                })}
            >
                <lr-icon-chat size="medium"></lr-icon-chat>
                <p id="chat-minimized-text">${t('chatbot:minimized.label')}</p>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'lit-lr-chatbot-minimized': ChatbotMinimized;
    }
}
